import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, FileText, History, Pencil, Save, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * EDITAR LOS DATOS DEL CONTRATO + HISTORIAL (pedido de Manuel, 2026-08-21).
 *
 * Muestra los campos con que se llenó ESE contrato tal como están hoy en el PDF, los deja
 * corregir y, al guardar, el backend rehace el documento. El link que el homeowner tiene en su
 * correo pasa a mostrar el contrato corregido; el PDF no lleva ninguna marca del cambio.
 *
 * El rastro es INTERNO y vive en la pestaña Historial: cada corrección con su PDF anterior,
 * qué cambió, quién y cuándo. El homeowner nunca ve esta pantalla.
 */
type Campo = { key: string; value: string; on_document: boolean };
type FieldsView = {
  id: string; status: string; kind: string; signed: boolean;
  pdf_locked: boolean; fields: Campo[]; versions: number;
};
type Cambio = { key: string; from: string; to: string };
type Version = {
  id: string; version: number; created_at: string; status: string | null;
  note: string | null; by: string | null; changes: Cambio[]; url: string | null;
};

const fmt = (s: string) => new Date(s).toLocaleString('en-US');

export function ContractEditModal({ contractId, onClose, onSaved }: {
  contractId: string; onClose: () => void; onSaved?: () => void;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'edit' | 'history'>('edit');
  const [data, setData] = useState<FieldsView | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [avisar, setAvisar] = useState(false);
  const [hist, setHist] = useState<{ current: Version; versions: Version[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api.get<FieldsView>(`/api/contracts/${contractId}/fields`)
      .then((r) => { if (!vivo) return; setData(r); setDraft(Object.fromEntries(r.fields.map((f) => [f.key, f.value]))); })
      .catch((e) => { if (vivo) setErr(e instanceof ApiError ? e.message : 'Error'); });
    return () => { vivo = false; };
  }, [contractId]);

  // El historial se carga al abrir la pestaña (y después de cada guardado).
  async function cargarHistorial() {
    try { setHist(await api.get<{ current: Version; versions: Version[] }>(`/api/contracts/${contractId}/versions`)); }
    catch { /* el historial no bloquea la edición */ }
  }
  useEffect(() => { if (tab === 'history' && !hist) cargarHistorial(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const sucio = !!data && data.fields.some((f) => (draft[f.key] ?? '') !== f.value);

  const ERR: Record<string, string> = {
    contract_voided: t('cxe.errVoided'),
    contract_not_found: t('cxe.errNotFound'),
    template_not_found: t('cxe.errNoTemplate'),
  };

  async function guardar() {
    if (!data || !sucio) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await api.put<{ changed: boolean; version: number | null; emailed: boolean }>(
        `/api/contracts/${contractId}/fields`,
        { values: draft, note: note.trim() || undefined, notifyHomeowner: avisar },
      );
      // Se recarga desde el servidor: los valores guardados son los que quedaron en el PDF.
      const fresco = await api.get<FieldsView>(`/api/contracts/${contractId}/fields`);
      setData(fresco);
      setDraft(Object.fromEntries(fresco.fields.map((f) => [f.key, f.value])));
      setNote('');
      setHist(null); // se vuelve a pedir con la versión nueva
      setOk(r.emailed ? t('cxe.savedAndSent') : t('cxe.saved'));
      onSaved?.();
    } catch (e) {
      setErr(e instanceof ApiError ? (ERR[e.message] ?? e.message) : 'Error');
    } finally { setBusy(false); }
  }

  // Etiqueta del campo: misma clave que usa el catálogo del backend (cxf.<key>).
  const label = (k: string) => t(`cxf.${k}`);

  return (
    <div className="cdw-mmodal-backdrop ash-modal-backdrop" onClick={onClose}>
      <div className="cdw-mmodal ash-modal cxe-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ash-modal-head">
          <b><Pencil size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cxe.title')}</b>
          <button type="button" className="btn ghost sm" onClick={onClose}><X size={15} strokeWidth={1.75} /></button>
        </div>

        {/* Pestañas: los datos vs el historial de correcciones. */}
        <div className="cxe-tabs">
          <button type="button" className={`btn ghost sm${tab === 'edit' ? ' cxe-tab-on' : ''}`} onClick={() => setTab('edit')}>
            <FileText size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cxe.tabData')}
          </button>
          <button type="button" className={`btn ghost sm${tab === 'history' ? ' cxe-tab-on' : ''}`} onClick={() => setTab('history')}>
            <History size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cxe.tabHistory')}{data?.versions ? ` (${data.versions})` : ''}
          </button>
        </div>

        {err && (
          <div className="cxe-alert cxe-alert-red">
            <AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {err}
          </div>
        )}
        {ok && <div className="cxe-alert cxe-alert-green">{ok}</div>}

        {tab === 'edit' && (
          <>
            {!data && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>}
            {data && (
              <>
                {/* Qué va a pasar exactamente, según dónde esté el contrato. */}
                <div className="muted cxe-hint">
                  {data.signed ? t('cxe.hintSigned') : t('cxe.hintDraft')}
                </div>
                {data.pdf_locked && (
                  <div className="cxe-alert cxe-alert-amber">
                    <AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cxe.hintPaper')}
                  </div>
                )}
                <div className="cxe-grid">
                  {data.fields.map((f) => (
                    <label key={f.key} className="cxe-field">
                      <span className="cxe-label">
                        {label(f.key)}
                        {!f.on_document && <span className="badge gray cxe-off">{t('cxe.notPrinted')}</span>}
                      </span>
                      <input
                        className="input"
                        value={draft[f.key] ?? ''}
                        maxLength={300}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      />
                    </label>
                  ))}
                </div>
                <label className="cxe-field">
                  <span className="cxe-label">{t('cxe.note')}</span>
                  <input className="input" value={note} maxLength={500} placeholder={t('cxe.notePh')} onChange={(e) => setNote(e.target.value)} />
                </label>
                {/* El correo que el cliente ya tiene lleva el PDF ADJUNTO: ese adjunto no cambia.
                    Reenviar la copia es una decisión de quien edita, nunca automática. */}
                {data.status === 'signed' && (
                  <label className="cxe-check">
                    <input type="checkbox" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} />
                    <span>{t('cxe.resendCopy')}</span>
                  </label>
                )}
                <div className="cxe-actions">
                  <button className="btn sm" disabled={busy || !sucio} onClick={guardar} style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }}>
                    <Save size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {busy ? '…' : t('common.save')}
                  </button>
                  <button className="btn ghost sm" onClick={onClose}>{t('common.cancel')}</button>
                </div>
              </>
            )}
          </>
        )}

        {tab === 'history' && (
          <div className="cxe-hist">
            {!hist && <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>}
            {hist && (
              <>
                <div className="cxe-vrow">
                  <div className="cxe-vhead">
                    <b>{t('cxe.vCurrent')} · v{hist.current.version}</b>
                    {hist.current.url && <a href={hist.current.url} target="_blank" rel="noopener noreferrer" className="cxe-vlink">{t('cx.view')} <ExternalLink size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></a>}
                  </div>
                </div>
                {hist.versions.length === 0 && (
                  <div className="muted" style={{ fontSize: 'var(--fs-sm)', padding: '8px 0' }}>{t('cxe.noVersions')}</div>
                )}
                {hist.versions.map((v) => (
                  <div key={v.id} className="cxe-vrow">
                    <div className="cxe-vhead">
                      <b>v{v.version}</b>
                      <span className="muted cxe-vwhen">{fmt(v.created_at)}{v.by ? ` · ${v.by}` : ''}</span>
                      {v.url && <a href={v.url} target="_blank" rel="noopener noreferrer" className="cxe-vlink">{t('cxe.vPdf')} <ExternalLink size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></a>}
                    </div>
                    {/* Qué se cambió AL PASAR de esta versión a la siguiente. */}
                    <div className="cxe-changes">
                      {v.changes.map((c, i) => (
                        <div key={i} className="cxe-change">
                          <span className="cxe-ckey">{label(c.key)}</span>
                          <span className="cxe-cfrom">{c.from || '—'}</span>
                          <span className="muted">→</span>
                          <span className="cxe-cto">{c.to || '—'}</span>
                        </div>
                      ))}
                    </div>
                    {v.note && <div className="muted cxe-vnote">“{v.note}”</div>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
